import { DiscordSDK } from '@discord/embedded-app-sdk';

const config = window.__BEATRA_ACTIVITY__ || {};
const PAGE_PARAMS = new URLSearchParams(window.location.search);
const elements = {
    artShell: document.querySelector('#art-shell'),
    artworkLayers: [
        document.querySelector('#artwork-a'),
        document.querySelector('#artwork-b'),
    ],
    artist: document.querySelector('#artist'),
    backdrop: document.querySelector('#backdrop'),
    connection: document.querySelector('#connection'),
    duration: document.querySelector('#duration'),
    diagnosticsGrid: document.querySelector('#diagnostics-grid'),
    elapsed: document.querySelector('#elapsed'),
    infoWidget: document.querySelector('#info-widget'),
    platform: document.querySelector('#platform'),
    playState: document.querySelector('#play-state'),
    progress: document.querySelector('#progress'),
    queue: document.querySelector('#queue'),
    queueList: document.querySelector('#queue-list'),
    queuePanel: document.querySelector('#queue-panel'),
    room: document.querySelector('#room'),
    statusPill: document.querySelector('#status-pill'),
    startupDiagnostics: document.querySelector('#startup-diagnostics'),
    title: document.querySelector('#title'),
    trackPanel: document.querySelector('#track-panel'),
    changeNotice: document.querySelector('#change-notice'),
    video: document.querySelector('#video'),
    videoLabel: document.querySelector('#video-label'),
    videoPreference: document.querySelector('#video-preference'),
    widgetToggle: document.querySelector('#widget-toggle'),
    widgetToggleLabel: document.querySelector('#widget-toggle-label'),
};

let context = { guildId: null, channelId: null };
let currentState = null;
let lastArtwork = '';
let lastVideo = '';
let failedVideo = '';
let lastQueueSignature = '';
let activeArtworkLayer = 0;
let presentedVideoTime = null;
let presentedVideoAt = 0;
let lastVideoSeekAt = 0;
let videoHeldForSync = false;
let videoAttemptStartedAt = 0;
let videoAttemptNumber = 0;
let videoRetryTimer = null;
let videoSourceTimer = null;
let videoCompatibilityMode = false;
let videoPlaybackBlocked = false;
let videoPlayPending = null;
let videoPlayRetryAt = 0;
let previousUiState = null;
let collapseWidgetTimer = null;
const HARD_SYNC_THRESHOLD_SECONDS = 0.08;
const SOFT_SYNC_THRESHOLD_SECONDS = 0.02;
const STATE_POLL_INTERVAL_MS = 250;
const VIDEO_SYNC_INTERVAL_MS = 40;
const VIDEO_RETRY_INTERVAL_MS = 1500;
const VIDEO_RETRY_WINDOW_MS = 30_000;
const MEDIA_FADE_MS = 650;
const WIDGET_EXPANDED_MS = 7_000;
const WIDGET_VISIBILITY_KEY = 'beatra.activity.widget-visible.v1';
const VIDEO_PREFERENCE_KEY = 'beatra.activity.video-preference.v1';
const DEBUG_VIDEO = PAGE_PARAMS.get('videoDebug') !== '0' &&
    PAGE_PARAMS.get('debugVideo') !== '0' &&
    config?.debugVideo !== false;
const VIDEO_SYNC_LOG_MIN_MS = 500;
const VIDEO_BUFFER_LOG_MIN_MS = 1_000;
const videoDebugState = {
    lastSyncLogAt: 0,
    lastBufferLogAt: 0,
    lastEventLogAt: 0,
    lastSourceLogAt: 0,
    lastCorrectionLogAt: 0,
};

function readWidgetVisibilityPreference() {
    try {
        const saved = localStorage.getItem(WIDGET_VISIBILITY_KEY);
        if (saved === 'true') return true;
        if (saved === 'false') return false;
    } catch {
        // Some hardened clients disable persistent storage. The toggle still
        // works for the lifetime of the current Activity iframe.
    }
    return null;
}

let widgetVisibilityPreference = readWidgetVisibilityPreference();

function normalizeVideoPreference(value) {
    return value === 'low' || value === 'artwork' ? value : 'auto';
}

function readVideoPreference() {
    try {
        return normalizeVideoPreference(localStorage.getItem(VIDEO_PREFERENCE_KEY));
    } catch {
        return 'auto';
    }
}

let videoPreference = readVideoPreference();

function applyVideoPreference() {
    elements.videoPreference.value = videoPreference;
    elements.artShell.classList.toggle('artwork-only', videoPreference === 'artwork');
}

function setVideoPreference(value) {
    const next = normalizeVideoPreference(value);
    if (next === videoPreference) return;
    videoPreference = next;
    try {
        localStorage.setItem(VIDEO_PREFERENCE_KEY, videoPreference);
    } catch {
        // This is a client preference, never a shared server setting. When
        // storage is blocked it still works for this Activity session.
    }
    applyVideoPreference();
    clearVideo(true);
    if (currentState?.active) syncVideoSource(currentState);
}

function videoReadyLabel() {
    if (videoPreference === 'artwork') return 'Artwork only · audio from Discord';
    return videoPreference === 'low'
        ? 'Lower-data video · audio from Discord'
        : 'HD video · audio from Discord';
}

function isWidgetVisible() {
    return getComputedStyle(elements.infoWidget).display !== 'none';
}

function updateWidgetToggle() {
    const visible = isWidgetVisible();
    elements.widgetToggle.setAttribute('aria-expanded', String(visible));
    elements.widgetToggle.title = visible ? 'Hide song information' : 'Show song information';
    elements.widgetToggleLabel.textContent = visible ? 'Hide info' : 'Show info';
}

function applyWidgetVisibilityPreference() {
    document.body.classList.toggle('widget-force-visible', widgetVisibilityPreference === true);
    document.body.classList.toggle('widget-force-hidden', widgetVisibilityPreference === false);
    requestAnimationFrame(updateWidgetToggle);
}

function setWidgetVisibility(visible) {
    widgetVisibilityPreference = Boolean(visible);
    try {
        localStorage.setItem(WIDGET_VISIBILITY_KEY, String(widgetVisibilityPreference));
    } catch {
        // Keep the in-memory preference when storage is unavailable.
    }
    applyWidgetVisibilityPreference();
}

function formatTime(milliseconds) {
    const seconds = Math.max(0, Math.floor((Number(milliseconds) || 0) / 1000));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainder = seconds % 60;
    return hours
        ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
        : `${minutes}:${String(remainder).padStart(2, '0')}`;
}

function formatLatency(milliseconds) {
    const value = Math.max(0, Number(milliseconds) || 0);
    return value >= 1_000 ? `${(value / 1_000).toFixed(2)}s` : `${Math.round(value)}ms`;
}

function describePlaybackSource(source) {
    return ({
        'cached-file': 'temporary disk preload',
        'download-fallback': 'exact YouTube download',
        'pre-resolved-youtube': 'reused YouTube resolution',
        'youtube-direct-ffmpeg': 'fresh signed YouTube stream',
        'direct-stream': 'direct provider stream',
    })[source] || String(source || 'provider stream').replaceAll('-', ' ');
}

function maybeVideoLog(category, message, intervalMs = 0) {
    if (!DEBUG_VIDEO) return;
    const now = performance.now();
    if (intervalMs > 0 && now - (videoDebugState[`last${category}LogAt`] || 0) < intervalMs) return;
    videoDebugState[`last${category}LogAt`] = now;
    console.log(`[activity-video:${category}] ${message}`);
}

function getVideoBufferAhead() {
    const buffered = elements.video?.buffered;
    if (!buffered || buffered.length === 0) return 0;
    const currentTime = elements.video.currentTime || 0;
    for (let i = 0; i < buffered.length; i += 1) {
        if (currentTime >= buffered.start(i) && currentTime <= buffered.end(i)) {
            return Math.max(0, buffered.end(i) - currentTime);
        }
    }
    const firstStart = buffered.start(0);
    return currentTime < firstStart ? Math.max(0, buffered.end(0) - currentTime) : 0;
}

function logBufferState(context, intervalMs = VIDEO_BUFFER_LOG_MIN_MS) {
    maybeVideoLog(
        'Buffer',
        `${context} ready=${elements.video.readyState} ` +
        `paused=${elements.video.paused} buffering=${elements.video.readyState === 3 ? 'true' : 'false'} ` +
        `time=${elements.video.currentTime.toFixed(2)} drift=${currentState?.active ? (currentState.positionMs - elements.video.currentTime * 1000).toFixed(0) : 'n/a'}ms ` +
        `ahead=${getVideoBufferAhead().toFixed(2)}s`,
        intervalMs
    );
}

function renderStartupDiagnostics(startup) {
    if (!startup) {
        elements.startupDiagnostics.hidden = true;
        elements.diagnosticsGrid.replaceChildren();
        return;
    }
    const rows = [
        [startup.kind === 'transition' ? 'Track transition' : 'Command to audible audio', formatLatency(startup.totalMs)],
        startup.lookupMs == null ? null : ['Search / metadata', formatLatency(startup.lookupMs)],
        startup.voiceMs == null ? null : ['Voice connection', formatLatency(startup.voiceMs)],
        ['Audio preparation', formatLatency(startup.audioPrepareMs)],
        startup.discordReadyMs == null ? null : ['Discord packet start', formatLatency(startup.discordReadyMs)],
        ['Playback path', describePlaybackSource(startup.source)],
    ].filter(Boolean);
    const fragment = document.createDocumentFragment();
    for (const [label, value] of rows) {
        const name = document.createElement('span');
        const result = document.createElement('strong');
        name.textContent = label;
        result.textContent = value;
        fragment.append(name, result);
    }
    elements.diagnosticsGrid.replaceChildren(fragment);
    elements.startupDiagnostics.hidden = false;
}

function currentPosition(state) {
    if (!state?.active) return 0;
    const elapsed = state.paused
        ? 0
        : Math.max(0, performance.now() - (state.receivedAtMs || performance.now()));
    const requestTransit = state.paused ? 0 : (state.transitCompensationMs || 0);
    return Math.min(state.durationMs || Infinity, state.positionMs + requestTransit + elapsed);
}

function updateProgress() {
    const position = currentPosition(currentState);
    const duration = currentState?.durationMs || 0;
    elements.elapsed.textContent = formatTime(position);
    elements.duration.textContent = formatTime(duration);
    elements.progress.style.width = duration ? `${Math.min(100, position / duration * 100)}%` : '0%';
    requestAnimationFrame(updateProgress);
}

function showIdle(reason) {
    currentState = null;
    clearVideo();
    elements.platform.textContent = 'Waiting for music';
    elements.title.textContent = reason === 'different_voice_channel'
        ? 'The bot is playing in another channel'
        : 'Start a song with /play';
    elements.artist.textContent = 'This visualizer will update automatically.';
    elements.room.textContent = 'Discord visualizer';
    elements.playState.textContent = 'Idle';
    renderStartupDiagnostics(null);
    elements.queue.textContent = 'Queue empty';
    renderQueue([]);
    elements.statusPill.classList.remove('live');
    elements.statusPill.querySelector('span').textContent = 'Waiting';
    elements.progress.style.width = '0%';
}

function getTrackKey(track) {
    return `${track?.title || ''}\u0000${track?.artist || ''}\u0000${track?.durationMs || 0}`;
}

function getQueueTokens(queuePreview) {
    const occurrences = new Map();
    return (queuePreview || []).map(track => {
        const key = getTrackKey(track);
        const occurrence = (occurrences.get(key) || 0) + 1;
        occurrences.set(key, occurrence);
        return `${key}\u0000${occurrence}`;
    });
}

function detectUiChanges(state) {
    const queuePreview = Array.isArray(state?.queuePreview) ? state.queuePreview : [];
    const next = {
        active: Boolean(state?.active),
        paused: Boolean(state?.paused),
        queueLength: Number(state?.queueLength) || 0,
        queueTokens: getQueueTokens(queuePreview),
        trackKey: state?.active ? getTrackKey(state) : '',
    };
    const changes = [];
    const addedQueueTokens = new Set();

    if (!previousUiState) {
        if (next.active) changes.push({ type: 'track', label: 'Now playing' });
    } else {
        if (next.trackKey !== previousUiState.trackKey) {
            changes.push({
                type: 'track',
                label: next.active ? 'Now playing changed' : 'Playback ended',
            });
        }

        if (next.queueLength > previousUiState.queueLength) {
            const previousTokens = new Set(previousUiState.queueTokens);
            next.queueTokens.forEach(token => {
                if (!previousTokens.has(token)) addedQueueTokens.add(token);
            });
            const addedCount = next.queueLength - previousUiState.queueLength;
            const firstAddedIndex = next.queueTokens.findIndex(token => addedQueueTokens.has(token));
            const firstAddedTitle = queuePreview[firstAddedIndex]?.title;
            changes.push({
                type: 'queue-add',
                label: firstAddedTitle && addedCount === 1
                    ? `Added to queue: ${firstAddedTitle}`
                    : `${addedCount} songs added to queue`,
            });
        } else if (next.queueLength < previousUiState.queueLength) {
            changes.push({ type: 'queue', label: 'Queue updated' });
        } else if (next.queueTokens.join('|') !== previousUiState.queueTokens.join('|')) {
            changes.push({ type: 'queue', label: 'Queue order changed' });
        }

        if (next.active && next.paused !== previousUiState.paused) {
            changes.push({ type: 'playback', label: next.paused ? 'Playback paused' : 'Playback resumed' });
        }
    }

    previousUiState = next;
    return { addedQueueTokens, changes };
}

function pulseElement(element) {
    if (!element) return;
    element.classList.remove('change-highlight');
    void element.offsetWidth;
    element.classList.add('change-highlight');
    setTimeout(() => element.classList.remove('change-highlight'), 2_200);
}

function collapseWidget() {
    if (currentState?.active && !elements.artShell.classList.contains('video-ready')) {
        collapseWidgetTimer = null;
        return;
    }
    elements.infoWidget.classList.remove('expanded');
    elements.infoWidget.classList.add('compact');
    elements.changeNotice.textContent = '';
}

function keepWidgetExpandedForArtwork() {
    if (collapseWidgetTimer) clearTimeout(collapseWidgetTimer);
    collapseWidgetTimer = null;
    elements.infoWidget.classList.remove('compact');
    elements.infoWidget.classList.add('expanded');
}

function revealWidgetChanges(changes) {
    if (!changes.length) return;
    if (collapseWidgetTimer) clearTimeout(collapseWidgetTimer);
    elements.infoWidget.classList.remove('compact');
    elements.infoWidget.classList.add('expanded');
    elements.changeNotice.textContent = [...new Set(changes.map(change => change.label))].join(' · ');
    pulseElement(elements.changeNotice);

    if (changes.some(change => change.type === 'track')) pulseElement(elements.title);
    if (changes.some(change => change.type.startsWith('queue'))) pulseElement(elements.queuePanel);
    if (changes.some(change => change.type === 'playback')) pulseElement(elements.statusPill);
    collapseWidgetTimer = setTimeout(collapseWidget, WIDGET_EXPANDED_MS);
}

function renderQueue(queuePreview, addedQueueTokens = new Set()) {
    // ETA counts down with the audio clock. Only rebuild when its displayed
    // value changes, rather than reloading artwork every 250ms state poll.
    const signature = JSON.stringify((Array.isArray(queuePreview) ? queuePreview : []).map(track => ({
        ...track, eta: formatQueueEta(track.eta),
    })));
    if (signature === lastQueueSignature) return;
    lastQueueSignature = signature;
    elements.queueList.replaceChildren();
    if (!Array.isArray(queuePreview) || queuePreview.length === 0) {
        const empty = document.createElement('li');
        empty.className = 'queue-empty';
        empty.textContent = 'Nothing queued';
        elements.queueList.append(empty);
        return;
    }

    const fragment = document.createDocumentFragment();
    const queueTokens = getQueueTokens(queuePreview);
    queuePreview.forEach((track, index) => {
        const item = document.createElement('li');
        if (addedQueueTokens.has(queueTokens[index])) item.classList.add('queue-added');
        const artwork = document.createElement('div');
        const details = document.createElement('div');
        const title = document.createElement('strong');
        const artist = document.createElement('small');
        const eta = document.createElement('small');

        artwork.className = 'queue-artwork';
        artwork.textContent = String(index + 1).padStart(2, '0');
        if (track.artwork) {
            const image = document.createElement('img');
            image.alt = '';
            image.loading = 'lazy';
            image.src = track.artwork;
            image.addEventListener('load', () => artwork.classList.add('loaded'));
            image.addEventListener('error', () => image.remove());
            artwork.append(image);
        }
        title.textContent = track.title || 'Unknown track';
        if (track.isAutoplay) {
            item.classList.add('queue-autoplay');
            const badge = document.createElement('span');
            badge.className = 'autoplay-badge';
            badge.textContent = 'Autoplay';
            details.append(badge);
        }
        artist.textContent = `${track.artist || 'Unknown artist'} · ${formatTime(track.durationMs)}`;
        eta.className = 'queue-eta';
        eta.textContent = formatQueueEta(track.eta);
        details.append(title, artist);
        if (eta.textContent) details.append(eta);
        item.append(artwork, details);
        fragment.append(item);
    });
    elements.queueList.append(fragment);
}

function formatQueueEta(eta) {
    if (!eta) return '';
    if (eta.reason === 'paused') return 'Paused';
    if (eta.reason === 'loop-track') return 'Current track repeating';
    if (eta.reason === 'shuffle') return 'Shuffle on · order may change';
    if (eta.reason === 'idle') return 'Waiting for playback';
    if (eta.waitMs == null || !Number.isFinite(eta.waitMs)) return 'Time unknown';
    const wait = Math.max(0, eta.waitMs);
    const repeat = eta.reason === 'loop-queue' ? ' · repeat' : '';
    if (wait < 60_000) return (wait === 0 ? 'Up next' : `in ~${Math.max(10, Math.ceil(wait / 10_000) * 10)}s`) + repeat;
    const minutes = Math.ceil(wait / 60_000);
    if (minutes < 60) return `in ~${minutes}m${repeat}`;
    return `in ~${Math.floor(minutes / 60)}h ${minutes % 60}m${repeat}`;
}

function clearVideo(immediate = false) {
    if (videoRetryTimer) clearTimeout(videoRetryTimer);
    videoRetryTimer = null;
    if (videoSourceTimer) clearTimeout(videoSourceTimer);
    videoSourceTimer = null;
    if (DEBUG_VIDEO) maybeVideoLog('Source', `clear video lastVideo=${lastVideo || 'none'}`, 0);
    elements.artShell.classList.remove('has-video', 'video-ready', 'video-loading', 'video-unavailable');
    const unload = () => {
        elements.video.pause();
        elements.video.removeAttribute('src');
        delete elements.video.dataset.source;
        elements.video.load();
    };
    if (elements.video.currentSrc || elements.video.src) {
        if (immediate) unload();
        else videoSourceTimer = setTimeout(unload, MEDIA_FADE_MS);
    }
    lastVideo = '';
    failedVideo = '';
    videoAttemptStartedAt = 0;
    videoAttemptNumber = 0;
    videoCompatibilityMode = false;
    videoPlaybackBlocked = false;
    presentedVideoTime = null;
    presentedVideoAt = 0;
    lastVideoSeekAt = 0;
    videoHeldForSync = false;
    elements.video.playbackRate = 1;
    elements.videoLabel.textContent = videoReadyLabel();
}

function requestVideoPlayback(userInitiated = false) {
    const source = elements.video.src;
    if (videoPreference === 'artwork' || currentState?.paused || !source || failedVideo === lastVideo ||
        videoPlayPending?.source === source || (!userInitiated && Date.now() < videoPlayRetryAt)) return;
    if (DEBUG_VIDEO) {
        maybeVideoLog(
            'Play',
            `source-change source=${source} userInitiated=${Boolean(userInitiated)} paused=${elements.video.paused} blocked=${videoPlaybackBlocked}`,
            0
        );
    }
    elements.video.muted = true;
    elements.video.defaultMuted = true;
    elements.video.playsInline = true;
    const attempt = { source };
    videoPlayPending = attempt;
    let result;
    try { result = elements.video.play(); } catch (error) { result = Promise.reject(error); }
    Promise.resolve(result).then(() => {
        if (elements.video.src !== source) return;
        if (DEBUG_VIDEO) maybeVideoLog('Play', `play() resolved source=${source}`, 0);
        videoPlaybackBlocked = false;
        videoPlayRetryAt = 0;
    }).catch(error => {
        if (elements.video.src !== source) return;
        if (DEBUG_VIDEO) {
            maybeVideoLog('Play', `play() failed source=${source} reason=${error?.name || 'error'}`, 0);
        }
        videoPlaybackBlocked = error?.name === 'NotAllowedError';
        videoPlayRetryAt = Date.now() + 1500;
        elements.video.playbackRate = 1;
        elements.artShell.classList.remove('video-ready');
        if (error?.name === 'AbortError') return; // Source changes/seeks are not permission failures.
        if (videoPlaybackBlocked) {
            elements.videoLabel.textContent = 'Client blocked video autoplay · showing artwork';
        } else if (!videoCompatibilityMode) {
            videoCompatibilityMode = true;
            loadVideoAttempt(lastVideo);
        } else {
            retryVideoOrUseArtwork();
        }
    }).finally(() => {
        if (videoPlayPending === attempt) videoPlayPending = null;
    });
}

function displayedVideoTime() {
    if (
        Number.isFinite(presentedVideoTime) &&
        presentedVideoAt > 0 &&
        performance.now() - presentedVideoAt < 250
    ) {
        const elapsed = elements.video.paused ? 0 : (performance.now() - presentedVideoAt) / 1000;
        return presentedVideoTime + elapsed * elements.video.playbackRate;
    }
    return elements.video.currentTime;
}

function observeVideoFrame(_now, metadata) {
    presentedVideoTime = metadata.mediaTime;
    presentedVideoAt = performance.now();
    elements.video.requestVideoFrameCallback?.(observeVideoFrame);
}

function loadVideoAttempt(source) {
    if (videoPreference === 'artwork' || !source || source !== lastVideo || failedVideo === source) return;
    videoAttemptNumber++;
    const compatibility = videoCompatibilityMode ? '&compat=1' : '';
    const quality = videoPreference === 'low' ? '&quality=low' : '';
    if (DEBUG_VIDEO) {
        maybeVideoLog(
            'Source',
            `load attempt #${videoAttemptNumber} for ${source} compat=${videoCompatibilityMode ? '1' : '0'}`,
            0
        );
    }
    const separator = source.includes('?') ? '&' : '?';
    presentedVideoTime = null;
    presentedVideoAt = 0;
    videoPlaybackBlocked = false;
    videoPlayPending = null;
    videoPlayRetryAt = 0;
    elements.video.dataset.source = source;
    elements.video.src = `${source}${separator}attempt=${videoAttemptNumber}${compatibility}${quality}`;
    elements.video.load();
}

function retryVideoOrUseArtwork() {
    if (
        videoPreference === 'artwork' ||
        !lastVideo ||
        failedVideo === lastVideo ||
        elements.video.dataset.source !== lastVideo
    ) return;
    const elapsed = Date.now() - videoAttemptStartedAt;
    if (DEBUG_VIDEO) {
        maybeVideoLog(
            'Source',
            `retry check elapsed=${elapsed}ms window=${VIDEO_RETRY_WINDOW_MS} source=${lastVideo}`,
            0
        );
    }
    elements.artShell.classList.remove('video-ready');
    if (elapsed < VIDEO_RETRY_WINDOW_MS) {
    const remaining = Math.max(1, Math.ceil((VIDEO_RETRY_WINDOW_MS - elapsed) / 1000));
        elements.videoLabel.textContent = `Finding video · artwork for up to ${remaining}s`;
        elements.artShell.classList.add('video-loading');
        if (videoRetryTimer) clearTimeout(videoRetryTimer);
        videoRetryTimer = setTimeout(() => loadVideoAttempt(lastVideo), VIDEO_RETRY_INTERVAL_MS);
        return;
    }

    failedVideo = lastVideo;
    if (DEBUG_VIDEO) {
        maybeVideoLog('Source', `video unavailable after retries source=${lastVideo}`, 0);
    }
    elements.videoLabel.textContent = 'No video found · showing artwork';
    elements.artShell.classList.remove('has-video', 'video-loading', 'video-ready');
    elements.artShell.classList.add('video-unavailable');
}

function correctVideoSync() {
    const state = currentState;
    if (
        videoPreference === 'artwork' ||
        !state?.active ||
        !state.video ||
        failedVideo === state.video ||
        elements.video.dataset.source !== state.video ||
        elements.video.readyState < HTMLMediaElement.HAVE_METADATA
    ) {
        return;
    }

    const desired = currentPosition(state) / 1000;
    const drift = desired - displayedVideoTime();
    const absDrift = Number.isFinite(drift) ? Math.abs(drift) : 0;
    const now = performance.now();
    if (DEBUG_VIDEO && (absDrift > SOFT_SYNC_THRESHOLD_SECONDS)) {
        maybeVideoLog(
            'Sync',
            `drift=${drift.toFixed(3)}s state=${state.paused ? 'paused' : 'playing'} ` +
            `ready=${elements.video.readyState} paused=${elements.video.paused} playbackRate=${elements.video.playbackRate.toFixed(2)} ` +
            `videoTime=${displayedVideoTime()?.toFixed(2)} desired=${desired.toFixed(2)}`,
            VIDEO_SYNC_LOG_MIN_MS
        );
    } else if (now - (videoDebugState.lastSyncLogAt || 0) > 2_000) {
        maybeVideoLog(
            'Sync',
            `drift=${Number.isFinite(drift) ? drift.toFixed(3) : 'n/a'} ready=${elements.video.readyState} ` +
            `bufferAhead=${getVideoBufferAhead().toFixed(2)}s`,
            2_000
        );
    }

    if (state.paused) {
        elements.video.playbackRate = 1;
        videoHeldForSync = false;
        if (!elements.video.paused) elements.video.pause();
    } else if (Number.isFinite(desired)) {
        if (Math.abs(drift) > HARD_SYNC_THRESHOLD_SECONDS) {
            if (performance.now() - lastVideoSeekAt > 140) {
                try {
                    elements.video.currentTime = desired;
                    if (DEBUG_VIDEO) {
                        maybeVideoLog(
                            'Sync',
                            `seek correction drift=${drift.toFixed(3)} target=${desired.toFixed(3)} current=${displayedVideoTime()?.toFixed(3)}`,
                            0
                        );
                    }
                    lastVideoSeekAt = performance.now();
                    presentedVideoTime = null;
                    videoHeldForSync = false;
                } catch { /* wait until seekable */ }
            }
            elements.video.playbackRate = 1;
        } else if (drift < -SOFT_SYNC_THRESHOLD_SECONDS) {
            // Video is ahead, so hold its muted frames briefly while Discord
            // audio catches up. The audio clock and speed are never changed.
            elements.video.playbackRate = 1;
            if (!elements.video.paused) elements.video.pause();
            videoHeldForSync = true;
        } else if (drift > SOFT_SYNC_THRESHOLD_SECONDS) {
            // Only muted Activity video may accelerate. It catches the Discord
            // audio master clock without touching or replacing the audio.
            elements.video.playbackRate = Math.min(1.08, 1 + drift * 0.8);
            if (elements.video.paused) requestVideoPlayback();
            videoHeldForSync = false;
            if (DEBUG_VIDEO && drift > 0.05) {
                maybeVideoLog(
                    'Sync',
                    `speed correction rate=${elements.video.playbackRate.toFixed(3)} drift=${drift.toFixed(3)}`,
                    VIDEO_SYNC_LOG_MIN_MS
                );
            }
        } else {
            elements.video.playbackRate = 1;
            if (videoHeldForSync || elements.video.paused) {
                requestVideoPlayback();
                if (DEBUG_VIDEO && now - (videoDebugState.lastCorrectionLogAt || 0) > 1_000) {
                    maybeVideoLog('Sync', 'resume after hold/seek path');
                    videoDebugState.lastCorrectionLogAt = now;
                }
            }
            videoHeldForSync = false;
        }
    }

    // Do not cross-fade away from the album cover merely because the first
    // video bytes arrived. Keep the cover visible through the initial seek,
    // then reveal the video only once its presented timeline matches audio.
    const hasPresentedFrame = typeof elements.video.requestVideoFrameCallback !== 'function' ||
        Number.isFinite(presentedVideoTime);
    if (
        !elements.artShell.classList.contains('video-ready') &&
        !elements.video.seeking &&
        !elements.video.paused &&
        !videoPlaybackBlocked &&
        elements.video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA &&
        hasPresentedFrame &&
        Math.abs(drift) <= HARD_SYNC_THRESHOLD_SECONDS
    ) {
        if (videoRetryTimer) clearTimeout(videoRetryTimer);
        videoRetryTimer = null;
        elements.videoLabel.textContent = videoReadyLabel();
        elements.artShell.classList.remove('video-loading', 'video-unavailable');
        elements.artShell.classList.add('video-ready');
        if (collapseWidgetTimer) clearTimeout(collapseWidgetTimer);
        collapseWidgetTimer = setTimeout(collapseWidget, 1_200);
    }
}

function syncVideoSource(state) {
    if (videoPreference === 'artwork') {
        if (lastVideo || elements.video.currentSrc || elements.video.src || videoRetryTimer || videoSourceTimer) clearVideo(true);
        elements.videoLabel.textContent = 'Artwork only · audio from Discord';
        keepWidgetExpandedForArtwork();
        return;
    }
    if (!state?.video) {
        if (DEBUG_VIDEO) maybeVideoLog('Source', `state has no video (audioReady=${state?.audioReady === false ? 'false' : 'true'})`, 1_000);
        if (lastVideo) clearVideo();
        elements.videoLabel.textContent = state?.audioReady === false
            ? 'Waiting for Discord audio · showing artwork'
            : 'No streamable video · showing artwork';
        elements.artShell.classList.toggle('video-unavailable', state?.audioReady !== false);
        keepWidgetExpandedForArtwork();
        return;
    }
    if (failedVideo === state.video) return;

    if (lastVideo !== state.video) {
        if (DEBUG_VIDEO) {
            maybeVideoLog(
                'Source',
                `video source changed old=${lastVideo || 'none'} new=${state.video}`,
                0
            );
        }
        if (videoRetryTimer) clearTimeout(videoRetryTimer);
        if (videoSourceTimer) clearTimeout(videoSourceTimer);
        const replacingVisibleVideo = Boolean(elements.video.currentSrc || elements.video.src);
        lastVideo = state.video;
        failedVideo = '';
        videoAttemptStartedAt = Date.now();
        videoAttemptNumber = 0;
        videoCompatibilityMode = false;
        videoPlaybackBlocked = false;
        elements.videoLabel.textContent = videoPreference === 'low'
            ? 'Finding lower-data video · showing artwork'
            : 'Finding HD video · showing artwork';
        elements.artShell.classList.remove('video-ready', 'video-unavailable');
        elements.artShell.classList.add('has-video', 'video-loading');
        keepWidgetExpandedForArtwork();
        elements.video.muted = true;
        // Keep the old decoded frame behind the artwork while it fades in.
        // Replacing src immediately turns the video element black mid-fade.
        videoSourceTimer = setTimeout(
            () => loadVideoAttempt(state.video),
            replacingVisibleVideo ? MEDIA_FADE_MS : 0
        );
    }
    correctVideoSync();
}

function setArtwork(url) {
    if (!url || url === lastArtwork) return;
    lastArtwork = url;
    const image = new Image();
    image.onload = () => {
        if (url !== lastArtwork) return;
        const incomingIndex = activeArtworkLayer === 0 ? 1 : 0;
        const incoming = elements.artworkLayers[incomingIndex];
        const outgoing = elements.artworkLayers[activeArtworkLayer];
        incoming.src = url;
        incoming.alt = 'Current album artwork';
        incoming.removeAttribute('aria-hidden');
        requestAnimationFrame(() => requestAnimationFrame(() => {
            if (url !== lastArtwork) return;
            incoming.classList.add('active');
            outgoing.classList.remove('active');
            outgoing.alt = '';
            outgoing.setAttribute('aria-hidden', 'true');
            activeArtworkLayer = incomingIndex;
        }));
        elements.backdrop.style.backgroundImage = `url("${url.replace(/"/g, '%22')}")`;
        elements.backdrop.classList.add('active');
    };
    image.onerror = () => {
        if (url !== lastArtwork || elements.artworkLayers[activeArtworkLayer].src) return;
        elements.backdrop.classList.remove('active');
    };
    image.src = url;
}

function render(state) {
    const { addedQueueTokens, changes } = detectUiChanges(state);
    if (!state?.active) {
        showIdle(state?.reason);
        revealWidgetChanges(changes);
        return;
    }
    currentState = state;
    elements.title.textContent = state.title;
    elements.title.classList.toggle('long-title', state.title.length > 58);
    elements.title.classList.toggle('very-long-title', state.title.length > 100);
    elements.artist.textContent = state.artist;
    elements.platform.textContent = state.isAutoplay ? `${state.platform} · Autoplay` : state.platform;
    elements.room.textContent = state.voiceChannelName;
    elements.playState.textContent = state.paused ? 'Paused' : 'Playing';
    renderStartupDiagnostics(state.startup);
    elements.queue.textContent = state.queueLength
        ? `${state.queueLength} ${state.queueLength === 1 ? 'track' : 'tracks'} queued`
        : 'Queue empty';
    renderQueue(state.queuePreview, addedQueueTokens);
    elements.statusPill.classList.toggle('live', !state.paused);
    elements.statusPill.querySelector('span').textContent = state.paused ? 'Paused' : 'Live';
    setArtwork(state.artwork);
    syncVideoSource(state);
    revealWidgetChanges(changes);
}

async function getDiscordContext() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('frame_id')) {
        return {
            guildId: params.get('guildId'),
            channelId: params.get('channelId'),
            mode: 'preview',
        };
    }

    const sdk = new DiscordSDK(config.clientId, { disableConsoleLogOverride: true });
    await sdk.ready();
    return {
        guildId: sdk.guildId,
        channelId: sdk.channelId,
        mode: 'discord',
    };
}

async function poll() {
    try {
        const requestStartedAtMs = performance.now();
        const query = new URLSearchParams();
        if (context.guildId) query.set('guildId', context.guildId);
        if (context.channelId) query.set('channelId', context.channelId);
        const response = await fetch(`./api/state?${query}`, { cache: 'no-store' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const state = await response.json();
        const receivedAtMs = performance.now();
        state.receivedAtMs = receivedAtMs;
        state.transitCompensationMs = Math.max(0, (receivedAtMs - requestStartedAtMs) / 2);
        const requestMs = receivedAtMs - requestStartedAtMs;
        if (DEBUG_VIDEO && requestMs > 250) {
            maybeVideoLog('Poll', `api/state latency=${Math.round(requestMs)}ms active=${state?.active}`, 1_000);
        }
        render(state);
        elements.connection.textContent = context.mode === 'discord' ? 'Connected to Discord' : 'Browser preview';
    } catch {
        elements.connection.textContent = 'Reconnecting…';
        if (DEBUG_VIDEO) maybeVideoLog('Poll', `api/state failed`, VIDEO_SYNC_LOG_MIN_MS);
    }
}

async function start() {
    applyWidgetVisibilityPreference();
    applyVideoPreference();
    elements.videoPreference.addEventListener('change', () => {
        setVideoPreference(elements.videoPreference.value);
    });
    elements.widgetToggle.addEventListener('click', () => {
        setWidgetVisibility(!isWidgetVisible());
    });
    window.addEventListener('resize', () => requestAnimationFrame(updateWidgetToggle), { passive: true });
    // Retry on ordinary client interaction if its autoplay policy requires one.
    // No dedicated video button and no change to Discord voice audio.
    const resumeVideo = () => {
        if (elements.video.paused && !videoHeldForSync) requestVideoPlayback(true);
    };
    document.addEventListener('pointerup', resumeVideo, { passive: true });
    document.addEventListener('keydown', resumeVideo);
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) { resumeVideo(); correctVideoSync(); }
    });
    elements.video.requestVideoFrameCallback?.(observeVideoFrame);
    elements.video.addEventListener('loadeddata', () => {
        if (DEBUG_VIDEO) maybeVideoLog('MediaEvent', 'loadeddata');
        logBufferState('loadeddata');
        correctVideoSync();
    });
    elements.video.addEventListener('loadedmetadata', () => {
        if (DEBUG_VIDEO) maybeVideoLog('MediaEvent', 'loadedmetadata');
        logBufferState('loadedmetadata');
        correctVideoSync();
    });
    elements.video.addEventListener('seeked', () => {
        if (DEBUG_VIDEO) maybeVideoLog('MediaEvent', 'seeked');
        logBufferState('seeked');
        correctVideoSync();
    });
    elements.video.addEventListener('canplay', () => {
        if (DEBUG_VIDEO) {
            maybeVideoLog('MediaEvent', `canplay ready=${elements.video.readyState}`);
            logBufferState('canplay');
        }
        correctVideoSync();
    });
    elements.video.addEventListener('waiting', () => logBufferState('waiting', 500));
    elements.video.addEventListener('stalled', () => logBufferState('stalled', 500));
    elements.video.addEventListener('progress', () => {
        if (DEBUG_VIDEO) logBufferState('progress', 1_500);
    });
    elements.video.addEventListener('playing', () => {
        videoPlaybackBlocked = false;
        if (DEBUG_VIDEO) {
            maybeVideoLog('MediaEvent', 'playing');
            logBufferState('playing', 500);
        }
    });
    elements.video.addEventListener('error', () => {
        if (videoPreference === 'artwork') return;
        if (DEBUG_VIDEO) {
            const code = elements.video.error?.code;
            const message = elements.video.error?.message || 'unknown';
            maybeVideoLog('MediaEvent', `error code=${code || 'n/a'} message=${message}`);
        }
        if (!videoCompatibilityMode && elements.video.error?.code !== 1) {
            videoCompatibilityMode = true;
            elements.videoLabel.textContent = 'Trying compatible video · audio continues normally';
            loadVideoAttempt(lastVideo);
            return;
        }
        retryVideoOrUseArtwork();
    });
    showIdle();
    try {
        context = await getDiscordContext();
    } catch {
        context = { guildId: null, channelId: null, mode: 'preview' };
        elements.connection.textContent = 'Discord connection unavailable';
    }
    await poll();
    setInterval(poll, STATE_POLL_INTERVAL_MS);
    setInterval(correctVideoSync, VIDEO_SYNC_INTERVAL_MS);
    requestAnimationFrame(updateProgress);
}

start();
